if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }


      /**
       * Check whether this product has selected
       * custom options connected to real Shopify variants.
       */
      getSelectedComponentVariants() {
        const componentItems = [];

        const selectors = document.querySelectorAll(
          'select[name^="custom_option_"]'
        );

        selectors.forEach((select) => {
          const selectedOption = select.options[select.selectedIndex];

          if (!selectedOption) return;

          const variantId =
            selectedOption.dataset.variantId ||
            select.dataset.selectedVariantId ||
            '';

          if (!variantId) return;

          const optionName =
            select.dataset.optionName ||
            `charm-${componentItems.length + 1}`;

          const sku =
            selectedOption.dataset.sku ||
            select.dataset.selectedSku ||
            '';

          componentItems.push({
            variantId: variantId,
            optionName: optionName,
            value: selectedOption.value,
            sku: sku,
          });
        });

        /*
         * Also support button/radio custom options
         * in case you use those on another charm product.
         */
        const radioGroups = {};

        document
          .querySelectorAll('input[type="radio"][name^="custom_option_"]:checked')
          .forEach((radio) => {
            const variantId = radio.dataset.variantId || '';

            if (!variantId) return;

            const groupName = radio.name;

            if (radioGroups[groupName]) return;

            radioGroups[groupName] = true;

            componentItems.push({
              variantId: variantId,
              optionName: radio.dataset.optionName || groupName,
              value: radio.value,
              sku: radio.dataset.sku || '',
            });
          });

        return componentItems;
      }


      /**
       * Get the existing line-item properties from
       * the necklace / bracelet product form.
       *
       * Example:
       * Charm 1 = Heart
       * Charm 2 = Padlock
       */
      getParentProperties(formData) {
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);

          if (match && value !== '') {
            properties[match[1]] = value;
          }
        }

        return properties;
      }


      /**
       * Normal Dawn Add to Cart.
       *
       * Used for every normal product on the website.
       */
      normalAddToCart(formData, config, evt) {
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartResponse(response, formData.get('id'));
          });
      }


      /**
       * Add the parent product plus the selected
       * real charm variants in ONE Shopify cart request.
       */
      addProductWithComponents(
        formData,
        config,
        componentItems,
        evt
      ) {
        const parentVariantId = formData.get('id');

        const quantity =
          parseInt(formData.get('quantity'), 10) || 1;

        const parentProperties =
          this.getParentProperties(formData);


        /*
         * Parent necklace / bracelet.
         */
        const items = [
          {
            id: Number(parentVariantId),
            quantity: quantity,
            properties: parentProperties,
          },
        ];


        /*
         * Add each selected charm as a genuine
         * Shopify merchandise line.
         *
         * _Charm Slot is deliberately unique for
         * each selection. This means that if a
         * customer chooses the same charm twice,
         * Shopify can retain the two selections
         * independently rather than accidentally
         * combining them.
         */
        componentItems.forEach((component, index) => {
          items.push({
            id: Number(component.variantId),
            quantity: quantity,

            properties: {
              '_Charm Slot': `Charm ${index + 1}`,
              '_Included With': parentVariantId,
            },
          });
        });


        const body = {
          items: items,
        };


        /*
         * Ask Shopify to return the updated Dawn
         * cart sections in the same request.
         */
        if (this.cart) {
          body.sections =
            this.cart
              .getSectionsToRender()
              .map((section) => section.id);

          body.sections_url =
            window.location.pathname;

          this.cart.setActiveElement(
            document.activeElement
          );
        }


        config.headers['Content-Type'] =
          'application/json';

        config.body = JSON.stringify(body);


        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartResponse(
              response,
              parentVariantId
            );
          });
      }


      /**
       * Handle Shopify's response and refresh
       * the Dawn cart drawer / notification.
       */
      handleCartResponse(response, productVariantId) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: productVariantId,
            errors:
              response.errors ||
              response.description,
            message: response.message,
          });

          this.handleErrorMessage(
            response.description ||
            response.message
          );

          const soldOutMessage =
            this.submitButton.querySelector(
              '.sold-out-message'
            );

          if (!soldOutMessage) return;

          this.submitButton.setAttribute(
            'aria-disabled',
            true
          );

          this.submitButtonText.classList.add(
            'hidden'
          );

          soldOutMessage.classList.remove(
            'hidden'
          );

          this.error = true;

          return;
        }


        if (!this.cart) {
          window.location =
            window.routes.cart_url;

          return;
        }


        const startMarker =
          CartPerformance.createStartingMarker(
            'add:wait-for-subscribers'
          );


        if (!this.error) {
          publish(
            PUB_SUB_EVENTS.cartUpdate,
            {
              source: 'product-form',
              productVariantId:
                productVariantId,
              cartData: response,
            }
          ).then(() => {
            CartPerformance.measureFromMarker(
              'add:wait-for-subscribers',
              startMarker
            );
          });
        }


        this.error = false;


        const quickAddModal =
          this.closest('quick-add-modal');


        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure(
                  'add:paint-updated-sections',
                  () => {
                    this.cart.renderContents(
                      response
                    );
                  }
                );
              });
            },
            { once: true }
          );

          quickAddModal.hide(true);
        } else {
          CartPerformance.measure(
            'add:paint-updated-sections',
            () => {
              this.cart.renderContents(response);
            }
          );
        }
      }


      /**
       * Main submit handler.
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }


        this.handleErrorMessage();


        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add(
          'loading'
        );

        this.querySelector(
          '.loading__spinner'
        ).classList.remove('hidden');


        const config =
          fetchConfig('javascript');

        config.headers['X-Requested-With'] =
          'XMLHttpRequest';


        const formData =
          new FormData(this.form);


        /*
         * Find all selected real Shopify
         * component variants.
         */
        const componentItems =
          this.getSelectedComponentVariants();


        let request;


        /*
         * If real component variants exist,
         * add parent + charms together.
         *
         * Otherwise preserve normal Dawn
         * behaviour.
         */
        if (componentItems.length > 0) {
          request =
            this.addProductWithComponents(
              formData,
              config,
              componentItems,
              evt
            );
        } else {
          delete config.headers[
            'Content-Type'
          ];

          request =
            this.normalAddToCart(
              formData,
              config,
              evt
            );
        }


        request
          .catch((e) => {
            console.error(e);

            this.handleErrorMessage(
              'There was a problem adding this item to your cart. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            this.querySelector(
              '.loading__spinner'
            ).classList.add('hidden');

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }


      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }


      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }


      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}